import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePasswordDisplay } from '@/hooks';
import { validatePassword, validatePasswords } from '@/utils/validatePassword';
import BaseInput from '../ui/BaseInput';
import ErrorBanner from '../ui/ErrorBanner';
import MaskPasswordButton from '../ui/shared/MaskPasswordButton';
import { SignUpFormData } from '../../types/auth';
import { trackEvent } from '@/utils/gtm';
import { GTM_EVENTS } from '@/constants/gtmEvents';

interface SignUpFormProps {
  onSubmit: (data: SignUpFormData) => void;
  loading?: boolean;
  onSwitchToLogin?: () => void;
  fieldErrors?: Record<string, string>;
  generalError?: string | null;
  feedbackBelowButton?: React.ReactNode;
}

const FUNCTION_OPTIONS: { value: string; labelKey: string; }[] = [
  { value: '', labelKey: 'selectFunction' },
  { value: 'Developer', labelKey: 'developer' },
  { value: 'Designer', labelKey: 'designer' },
  { value: 'Product Manager', labelKey: 'productManager' },
  { value: 'Data Scientist', labelKey: 'dataScientist' },
  { value: 'Marketing', labelKey: 'marketing' },
  { value: 'Other', labelKey: 'other' },
];

const SignUpForm = (props: SignUpFormProps) => {
  const { t } = useLanguage();
  const { onSubmit, loading, onSwitchToLogin, fieldErrors = {}, generalError } = props;
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [confirmPasswordErrors, setConfirmPasswordErrors] = useState<string[]>([]);
  const { showPassword, showConfirmPassword, setShowPassword, setShowConfirmPassword } = usePasswordDisplay();
  const [signupStarted, setSignupStarted] = useState(false);
  const [completedFields, setCompletedFields] = useState<Set<string>>(new Set());
  const [formData, setFormData] = React.useState<SignUpFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    function: '',
  });

  // Helper to map field to funnel percentage
  const getFunnelStep = (field: string): number => {
    const steps: Record<string, number> = {
      firstName: 20,
      lastName: 40,
      email: 60,
      password: 80,
      function: 90,
    };
    return steps[field] || 0;
  };

  const handleChange = (field: keyof SignUpFormData) => (value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Track signup start on first interaction
    if (!signupStarted && value.trim().length > 0) {
      setSignupStarted(true);
      trackEvent({
        event: GTM_EVENTS.SIGNUP_START,
      });
    }
  };

  const handleFieldBlur = (field: keyof SignUpFormData) => {
    const value = formData[field];
    
    // Only track once per field when user completes it
    if (value.trim().length > 0 && !completedFields.has(field) && field !== 'confirmPassword') {
      setCompletedFields(prev => new Set(prev).add(field));
      trackEvent({
        event: GTM_EVENTS.SIGNUP_FIELD_COMPLETE,
        field_name: field,
        funnel_step: getFunnelStep(field),
      });
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, function: value }));
    
    // Track signup start if not started yet
    if (!signupStarted && value) {
      setSignupStarted(true);
      trackEvent({
        event: GTM_EVENTS.SIGNUP_START,
      });
    }
  };

  const handleSelectBlur = () => {
    const value = formData.function;
    
    // Track function field completion
    if (value && !completedFields.has('function')) {
      setCompletedFields(prev => new Set(prev).add('function'));
      trackEvent({
        event: GTM_EVENTS.SIGNUP_FIELD_COMPLETE,
        field_name: 'function',
        funnel_step: getFunnelStep('function'),
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {generalError && (
        <ErrorBanner
          title={t("registrationFailed")}
          message={generalError}
          type="error"
          action={onSwitchToLogin ? { text: t('tryLoginInstead'), onClick: onSwitchToLogin } : undefined}
        />
      )}
      <BaseInput
        label="First Name"
        name="firstName"
        type="text"
        value={formData.firstName}
        onChange={e => handleChange('firstName')(e)}
        onBlur={() => handleFieldBlur('firstName')}
        error={fieldErrors.firstName}
        required
        placeholder={t("firstName")}
        autoComplete="given-name"
      />
      <BaseInput
        label="Last Name"
        name="lastName"
        type="text"
        value={formData.lastName}
        onChange={e => handleChange('lastName')(e)}
        onBlur={() => handleFieldBlur('lastName')}
        error={fieldErrors.lastName}
        required
        placeholder={t("lastName")}
        autoComplete="family-name"
      />
      <BaseInput
        label="Email Address"
        name="email"
        type="email"
        value={formData.email}
        onChange={e => handleChange('email')(e)}
        onBlur={() => handleFieldBlur('email')}
        error={fieldErrors.email}
        required
        placeholder="you@company.com"
        autoComplete="email"
      />
      <BaseInput
        label="Password"
        name="password"
        type={showPassword ? 'text' : 'password'}
        value={formData.password}
        onChange={e => {
          handleChange('password')(e);
          const errs = validatePassword(e);
          setPasswordErrors(errs);
        }}
        onBlur={() => handleFieldBlur('password')}
        error={
          passwordErrors.length > 0
            ? passwordErrors.map((err) => t(err)).join(', ')
            : fieldErrors.password
        }
        required
        placeholder={t("password")}
        autoComplete="new-password"
        rightAddon={<MaskPasswordButton showPassword={showPassword} setShowPassword={setShowPassword} />}
      />
      <BaseInput
        label="Confirm Password"
        name="confirmPassword"
        type={showConfirmPassword ? 'text' : 'password'}
        value={formData.confirmPassword}
        onChange={e => {
          handleChange('confirmPassword')(e);
          const errs = validatePasswords(formData.password, e);
          setPasswordErrors(errs.password);
          setConfirmPasswordErrors(errs.confirmPassword);
        }}
        error={
          confirmPasswordErrors.length > 0
            ? confirmPasswordErrors.map((err) => t(err)).join(', ')
            : fieldErrors.confirmPassword
        }
        required
        placeholder={t("confirmPassword")}
        autoComplete="new-password"
        rightAddon={<MaskPasswordButton showPassword={showConfirmPassword} setShowPassword={setShowConfirmPassword} />}
      />
      <div className="space-y-1">
        <select
          name="function"
          value={formData.function}
          onChange={handleSelectChange}
          onBlur={handleSelectBlur}
          required
          className={`w-full px-4 py-3 rounded-md border transition-all duration-200 ease-out appearance-none focus:outline-none focus:ring-1 focus:ring-brand-electric-blue focus:border-brand-electric-blue ${fieldErrors.function
            ? 'border-red-500 text-red-900 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300'
            }`}
          aria-invalid={!!fieldErrors.function}
          aria-describedby={fieldErrors.function ? 'function-error' : undefined}
        >
          {FUNCTION_OPTIONS.map((option) => (
            <option key={option.value} value={option.value} disabled={option.value === ''}>
              {t(option.labelKey)}
            </option>
          ))}
        </select>
        <div
          className={`overflow-hidden transition-all duration-200 ease-out ${fieldErrors.function ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'
            }`}
          id={fieldErrors.function ? 'function-error' : undefined}
          aria-live="polite"
        >
          {fieldErrors.function && (
            <div className="flex items-center space-x-2 mt-2 text-sm text-red-600">
              <svg className="h-4 w-4 flex-shrink-0 animate-bounce-gentle text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" /></svg>
              <span>{fieldErrors.function}</span>
            </div>
          )}
        </div>
      </div>
      <button
        type="submit"
        disabled={loading}
        className={`w-full bg-brand-dark-blue hover:bg-brand-electric-blue disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-md transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:ring-offset-2 ${loading ? 'cursor-not-allowed' : ''}`}
      >
        {loading ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>{t('creatingAccount')}</span>
          </div>
        ) : (
          t('createAccount')
        )}
      </button>
      {props.feedbackBelowButton}
      <p className="text-center text-sm text-gray-600 mt-2">
        {t('alreadyHaveAnAccount')}{' '}
        <a href="#" className="text-brand-electric-blue hover:text-brand-dark-blue" onClick={(e) => {
          e.preventDefault();
          onSwitchToLogin?.();
        }}>
          {t('login')}
        </a>
      </p>
    </form>
  );
};

export { SignUpForm };
